import type { ProjectContentMatch } from "@cmmd-forge/contracts";
import type { ReactNode, RefObject } from "react";

import { cn } from "~/lib/utils";

interface IndexedProjectContentMatch extends ProjectContentMatch {
  readonly resultIndex: number;
}

interface MatchGroup {
  readonly matches: ReadonlyArray<IndexedProjectContentMatch>;
  readonly path: string;
}

function groupProjectContentMatches(
  matches: ReadonlyArray<ProjectContentMatch>,
): ReadonlyArray<MatchGroup> {
  const groups = new Map<string, IndexedProjectContentMatch[]>();
  matches.forEach((match, resultIndex) => {
    const indexedMatch = { ...match, resultIndex };
    const group = groups.get(match.path);
    if (group) {
      group.push(indexedMatch);
      return;
    }
    groups.set(match.path, [indexedMatch]);
  });
  return [...groups].map(([path, groupedMatches]) => ({ path, matches: groupedMatches }));
}

function splitPath(path: string): { readonly directory: string; readonly name: string } {
  const separator = path.lastIndexOf("/");
  return separator === -1
    ? { name: path, directory: "" }
    : { name: path.slice(separator + 1), directory: path.slice(0, separator) };
}

function highlightedLine(match: ProjectContentMatch): ReactNode {
  let cursor = 0;
  const fragments: ReactNode[] = [];
  const occurrenceByRange = new Map<string, number>();
  match.matchRanges.forEach((range) => {
    const start = Math.max(cursor, Math.min(range.start, match.lineContent.length));
    const end = Math.max(start, Math.min(range.end, match.lineContent.length));
    if (start > cursor) fragments.push(match.lineContent.slice(cursor, start));
    if (end > start) {
      const rangeKey = `${start}:${end}`;
      const occurrence = occurrenceByRange.get(rangeKey) ?? 0;
      occurrenceByRange.set(rangeKey, occurrence + 1);
      fragments.push(
        <mark
          className="rounded-sm bg-warning-surface px-px text-inherit"
          key={occurrence === 0 ? rangeKey : `${rangeKey}:${occurrence}`}
        >
          {match.lineContent.slice(start, end)}
        </mark>,
      );
    }
    cursor = end;
  });
  if (cursor < match.lineContent.length) fragments.push(match.lineContent.slice(cursor));
  return fragments.length > 0 ? fragments : match.lineContent;
}

export function ProjectContentSearchResults(props: {
  readonly disabled: boolean;
  readonly matches: ReadonlyArray<ProjectContentMatch>;
  readonly onOpenMatch: (match: ProjectContentMatch) => void;
  readonly onSelect: (index: number) => void;
  readonly selectedIndex: number;
  readonly selectedResultRef: RefObject<HTMLButtonElement | null>;
}) {
  const groups = groupProjectContentMatches(props.matches);
  return (
    <div aria-label="Search results" className="min-h-0 flex-1 overflow-auto py-2">
      {groups.map((group) => {
        const path = splitPath(group.path);
        return (
          <section className="pb-2" key={group.path}>
            <div className="sticky top-0 z-10 flex min-w-0 items-center gap-2 border-y border-border bg-popover/95 px-3 py-2 text-xs backdrop-blur-sm">
              <span className="min-w-0 truncate font-medium text-foreground">{path.name}</span>
              {path.directory ? (
                <span className="min-w-0 truncate text-muted-foreground">{path.directory}</span>
              ) : null}
              <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
                {group.matches.length}
              </span>
            </div>
            {group.matches.map((match) => (
              <button
                aria-current={match.resultIndex === props.selectedIndex ? "true" : undefined}
                aria-label={`Open ${match.path} at line ${match.lineNumber}`}
                className={cn(
                  "flex min-h-touch w-full min-w-0 touch-manipulation items-center gap-3 px-3 text-left font-mono text-sm transition-colors md:min-h-7 md:text-xs",
                  "hover:bg-accent/60 focus-visible:bg-accent focus-visible:outline-none",
                  match.resultIndex === props.selectedIndex
                    ? "bg-accent text-accent-foreground"
                    : "",
                )}
                data-content-search-result={match.resultIndex}
                disabled={props.disabled}
                key={`${match.path}:${match.lineNumber}:${match.resultIndex}`}
                onClick={() => props.onOpenMatch(match)}
                onFocus={() => props.onSelect(match.resultIndex)}
                onMouseEnter={() => props.onSelect(match.resultIndex)}
                ref={match.resultIndex === props.selectedIndex ? props.selectedResultRef : null}
                type="button"
              >
                <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground/70">
                  {match.lineNumber}
                </span>
                <span className="min-w-0 flex-1 truncate whitespace-pre">
                  {highlightedLine(match)}
                </span>
              </button>
            ))}
          </section>
        );
      })}
    </div>
  );
}
